/**
 * Утилиты для работы с аватарами
 */
import { AVATAR_CONFIG } from '../config';

// Палитра красивых градиентов для аватаров
const AVATAR_COLORS = [
  'from-blue-500 to-blue-600',
  'from-purple-500 to-purple-600', 
  'from-pink-500 to-pink-600',
  'from-red-500 to-red-600',
  'from-orange-500 to-orange-600',
  'from-yellow-500 to-yellow-600',
  'from-green-500 to-green-600',
  'from-teal-500 to-teal-600',
  'from-cyan-500 to-cyan-600',
  'from-indigo-500 to-indigo-600',
  'from-violet-500 to-violet-600',
  'from-fuchsia-500 to-fuchsia-600',
  'from-rose-500 to-rose-600',
  'from-emerald-500 to-emerald-600',
  'from-lime-500 to-lime-600',
  'from-amber-500 to-amber-600',
  'from-slate-500 to-slate-600',
  'from-gray-500 to-gray-600'
];

/**
 * Получить стабильный цвет для имени (один и тот же для одного имени)
 * @param name - имя пользователя
 * @returns CSS класс градиента
 */
export function getAvatarColor(name: string): string {
  if (!name) return AVATAR_COLORS[0];
  
  // Создаем простой хеш из имени для стабильности цвета
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    const char = name.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Конвертируем в 32-битное целое
  }
  
  // Используем абсолютное значение хеша для индекса
  const colorIndex = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[colorIndex];
}

/**
 * Получить инициалы из имени
 * @param name - полное имя
 * @returns инициалы (максимум 2 символа)
 */
export function getInitials(name: string): string {
  if (!name) return '?';
  
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Получить правильный URL для аватара
 * @param avatarUrl - URL аватара (может быть полным URL или именем файла)
 * @returns правильный URL для отображения
 */
export function getAvatarUrl(avatarUrl: string | null | undefined): string | null {
  if (!avatarUrl) return null;
  
  // Если это уже полный URL (начинается с http), возвращаем как есть
  if (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://')) {
    return avatarUrl;
  }
  
  // Умное определение API базового URL
  function detectApiBaseUrl(): string {
    // If environment variable is set, use it
    if ((import.meta as any).env?.VITE_API_URL) {
      return (import.meta as any).env.VITE_API_URL;
    }
    
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    const port = window.location.port;
    
    // Always use the same hostname and port as frontend
    // API requests go through nginx proxy to backend
    return `${protocol}//${hostname}${port ? ':' + port : ''}`;
  }
  
  // Если путь уже начинается с /uploads/, используем как есть
  if (avatarUrl.startsWith('/uploads/')) {
    const apiBaseUrl = detectApiBaseUrl();
    return `${apiBaseUrl}${avatarUrl}`;
  }
  
  // Если это только имя файла, создаем полный URL
  const apiBaseUrl = detectApiBaseUrl();
  return `${apiBaseUrl}/uploads/avatars/${avatarUrl}`;
}

/**
 * Append a cache-bust query param without breaking URLs that already have query params.
 */
export function withAvatarCacheBust(url: string): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}t=${Date.now()}`;
}

/**
 * Проверить, является ли URL аватара внешним (UI Avatars)
 * @param avatarUrl - URL аватара
 * @returns true если это внешний URL
 */
export function isExternalAvatar(avatarUrl: string | null | undefined): boolean {
  if (!avatarUrl) return false;
  return avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://');
}

/**
 * Сгенерировать URL для UI Avatar
 * @param name - имя для генерации аватара
 * @param size - размер аватара
 * @param background - цвет фона
 * @param color - цвет текста
 * @returns URL для UI Avatar
 */
export function generateUIAvatarUrl(
  name: string, 
  size: number = 200, 
  background?: string, 
  color?: string
): string {
  return AVATAR_CONFIG.generateUIAvatar(name, size, background, color);
}
